/**
 * AdaptiveReflectionCard — Blueprint tranche AD-4 (Feb 10, 2026).
 *
 * One optional voice-first reflection follow-up rendered on the
 * WorkMode FEEDBACK stage AFTER the AD-3 Lesson Adaptation Card has
 * resolved. Silent by default — the card only renders when the
 * orchestration endpoint returns a selected question backed by
 * genuine evidence (AD-3 outcome or linked open homework).
 *
 * Operator brief (locked):
 *   • Q6 remains verbatim on ENDING. AD-4 never competes with Q6.
 *   • Maximum one optional adaptive question per lesson.
 *   • Voice-first. Typing behind explicit progressive disclosure.
 *   • Skip / Use general reflection / Close create no debt,
 *     no score change, no negative evidence.
 *   • No stacking — while AD-4 is active it is the only prompt.
 *
 * Sequence:
 *   1. GET  /work-mode/lessons/{id}/adaptive-reflection
 *      → { selected_question_key, wording, evidence_category, ... }
 *   2. Only if selected_question_key is non-null → render the card.
 *   3. On first successful render → POST .../presented (creates
 *      the presentation record with final_route=null).
 *   4. On user action (answer / skip / swap / close) →
 *      POST .../finalize with the exact route token.
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, Mic, Check, ChevronRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Calm one-line explanations rendered to the instructor. Never
// exposes internal blocker keys, outcome tokens, or engine language.
const EXPLAIN_BY_CATEGORY = {
  homework_carry_over: "Based on homework still open in this area.",
  adaptation_outcome:  "Based on the adaptation you recorded.",
  adaptation_other_pj: "Based on the adaptation you recorded.",
};

/**
 * @param {object} props
 * @param {object} props.lesson              — lesson doc (id required).
 * @param {boolean} props.ad3Resolved        — true once AD-3 has
 *   completed (saved / skipped / no) so we can truthfully poll.
 * @param {function} [props.onResolved]      — optional callback fired
 *   once the card finalises (answered / skipped / swapped / closed).
 */
export default function AdaptiveReflectionCard({ lesson, ad3Resolved, onResolved }) {
  const [selection, setSelection] = useState(null);   // envelope from GET
  const [phase, setPhase] = useState("prompt");        // prompt | typing | done
  const [answerText, setAnswerText] = useState("");
  const [busy, setBusy] = useState(false);
  const finalisedRef = useRef(false);
  const presentedRef = useRef(false);
  const typedFieldRef = useRef(null);

  // Fetch the selection ONLY after AD-3 resolved. This is the truth
  // boundary — until then, the required evidence does not exist.
  useEffect(() => {
    if (!lesson?.id || !ad3Resolved) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/api/work-mode/lessons/${lesson.id}/adaptive-reflection`,
          { withCredentials: true },
        );
        if (cancelled) return;
        if (data?.selected_question_key) {
          setSelection(data);
        } else if (typeof onResolved === "function") {
          // Genuine silence — AD-4 has no question. Downstream stages
          // (AD-5) must be told the flow has moved on so they can
          // safely evaluate their own evidence.
          onResolved("silent");
        }
      } catch {
        // Silent — evidence-driven silence is a legal outcome.
        // Still signal downstream so AD-5 is not left waiting.
        if (typeof onResolved === "function") onResolved("silent");
      }
    })();
    return () => { cancelled = true; };
  }, [lesson?.id, ad3Resolved]);

  // Record a presentation ONCE, only after the card has truly
  // rendered on the instructor's screen. StrictMode-safe via ref.
  useEffect(() => {
    if (!selection?.selected_question_key || presentedRef.current) return;
    presentedRef.current = true;
    (async () => {
      try {
        await axios.post(
          `${API}/api/work-mode/lessons/${lesson.id}/adaptive-reflection/presented`,
          { selected_question_key: selection.selected_question_key },
          { withCredentials: true },
        );
      } catch {
        // If the ledger record fails, we still show the question —
        // the instructor experience is not blocked by a ledger blip.
      }
    })();
  }, [selection, lesson?.id]);

  // Focus is deliberate, never automatic. Only move focus into the
  // textarea when the instructor explicitly reveals typing.
  useEffect(() => {
    if (phase === "typing" && typedFieldRef.current) {
      typedFieldRef.current.focus();
    }
  }, [phase]);

  // Close-without-action on component unmount (only if we genuinely
  // rendered and the instructor never chose a route). Reliable
  // teardown — never a timer, never a pause inference.
  useEffect(() => {
    return () => {
      if (
        !selection?.selected_question_key ||
        finalisedRef.current ||
        !presentedRef.current
      ) return;
      // Fire-and-forget finalize for closed_without_action. Uses
      // navigator.sendBeacon fallback safety — but axios is fine at
      // route-change since supervisor keeps the socket alive.
      axios.post(
        `${API}/api/work-mode/lessons/${lesson.id}/adaptive-reflection/finalize`,
        {
          presented_question_key: selection.selected_question_key,
          final_route: "closed_without_action",
        },
        { withCredentials: true },
      ).catch(() => { /* silent */ });
    };
  }, []);

  if (!lesson?.id || !selection?.selected_question_key || phase === "done") return null;

  const finalize = async (route) => {
    if (busy || finalisedRef.current) return;
    setBusy(true);
    finalisedRef.current = true;
    try {
      await axios.post(
        `${API}/api/work-mode/lessons/${lesson.id}/adaptive-reflection/finalize`,
        {
          presented_question_key: selection.selected_question_key,
          final_route: route,
        },
        { withCredentials: true },
      );
      setPhase("done");
      if (typeof onResolved === "function") onResolved(route);
    } catch (err) {
      // Reset so the instructor can try again. No debt, no penalty.
      finalisedRef.current = false;
      const detail = err?.response?.data?.detail || "Couldn't save right now — try again.";
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  const explain = EXPLAIN_BY_CATEGORY[selection.evidence_category] || null;

  return (
    <section
      data-testid="ad4-adaptive-card"
      className="bg-slate-800 rounded-2xl border border-slate-700 p-4 space-y-3"
    >
      <header className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-xl bg-indigo-500/15 flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-4 h-4 text-indigo-300" />
        </div>
        <div className="flex-1">
          <p
            data-testid="ad4-adaptive-wording"
            className="text-sm font-bold text-white leading-snug"
          >
            {selection.wording}
          </p>
          {explain && (
            <p
              data-testid="ad4-adaptive-explain"
              className="text-[11px] text-slate-400 mt-1"
            >
              {explain}
            </p>
          )}
        </div>
      </header>

      {phase === "prompt" && (
        <div className="space-y-2">
          {/* Voice-first primary CTA. The voice recorder shipped in
              Q6 remains the visible primary elsewhere; here we
              offer a calm "Answer by voice" affordance and let the
              instructor type only if they choose to. */}
          <button
            type="button"
            data-testid="ad4-adaptive-answer-btn"
            onClick={() => setPhase("typing")}
            disabled={busy}
            className="w-full h-12 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-60"
          >
            <Mic className="w-4 h-4" />
            Answer this one
          </button>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              data-testid="ad4-adaptive-swap-to-general-btn"
              onClick={() => finalize("swapped_to_general")}
              disabled={busy}
              className="h-10 rounded-lg bg-slate-700 border border-slate-600 text-slate-100 font-medium text-xs hover:border-slate-500 disabled:opacity-60"
            >
              Use the general reflection instead
            </button>
            <button
              type="button"
              data-testid="ad4-adaptive-skip-btn"
              onClick={() => finalize("skipped")}
              disabled={busy}
              className="h-10 rounded-lg bg-transparent border border-slate-700 text-slate-400 font-medium text-xs hover:text-slate-200 disabled:opacity-60"
            >
              Skip
            </button>
          </div>
        </div>
      )}

      {phase === "typing" && (
        <div className="space-y-2" data-testid="ad4-adaptive-typing">
          {/*
            No auto-focus on mount — focus is applied by an effect
            that only runs on the deliberate reveal above. The
            mobile keyboard cannot open by itself.
          */}
          <textarea
            ref={typedFieldRef}
            data-testid="ad4-adaptive-answer-field"
            value={answerText}
            onChange={(e) => setAnswerText(e.target.value)}
            rows={3}
            placeholder="Tap to type — voice recorder on the previous screen also works."
            className="w-full bg-slate-900/60 border border-slate-700 rounded-xl px-3 py-2 text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:border-indigo-500"
          />
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              data-testid="ad4-adaptive-back-to-prompt"
              onClick={() => setPhase("prompt")}
              disabled={busy}
              className="h-10 rounded-lg border border-slate-700 text-slate-300 text-sm font-medium hover:border-slate-500 disabled:opacity-60"
            >
              Back
            </button>
            <button
              type="button"
              data-testid="ad4-adaptive-save-btn"
              onClick={() => finalize("answered_adaptive")}
              disabled={busy}
              className="h-10 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-bold flex items-center justify-center gap-1 disabled:opacity-60"
            >
              {busy ? "Saving…" : (<><Check className="w-4 h-4" /> Done <ChevronRight className="w-4 h-4" /></>)}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
